import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Center,
  Divider,
  Group,
  Image,
  Loader,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal, AppModalSizes } from '../common/AppModal';
import { useModalZIndex } from '../../hooks/useModalZIndex';
import apiClient from '../../services/apiClient';
import { actionColors, brandColors } from '../../config/theme';
import { formatCalendarDate, formatInstantLocalShort } from '../../utils/formatDateTime';
import { OrderStatusBadges } from '../common/OrderStatusBadges';
import { formatStatusLabel } from '../../utils/orderStatusDisplay';
import { getStateName } from '../../constants/states';
import type { AdminGiftOrder } from '../../pages/admin/AdminGiftOrdersPage';
import { DemoAccountPill } from './DemoAccountPill';
import { AdminGiftOrderPricingSection } from './AdminGiftOrderPricingSection';
import { FulfillmentStatus, PaymentStatus } from '../../types';

interface AdminGiftOrderDetailModalProps {
  orderId: number | null;
  opened: boolean;
  onClose: () => void;
  onOrderUpdated?: () => void;
}

const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Group justify="space-between" wrap="nowrap">
    <Text fw={500} size="sm">
      {label}
    </Text>
    <div style={{ textAlign: 'right' }}>{children}</div>
  </Group>
);

export const AdminGiftOrderDetailModal = ({
  orderId,
  opened,
  onClose,
  onOrderUpdated,
}: AdminGiftOrderDetailModalProps) => {
  const zIndex = useModalZIndex(opened);
  const [order, setOrder] = useState<AdminGiftOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadOrder = (id: number) => {
    setLoading(true);
    setError(null);
    return apiClient
      .get<AdminGiftOrder>(`/admin/gift-orders/${id}`)
      .then((response) => {
        setOrder(response.data);
      })
      .catch(() => {
        setError('Could not load gift order detail.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!opened || !orderId) {
      setOrder(null);
      setError(null);
      setActionError(null);
      return;
    }

    void loadOrder(orderId);
  }, [opened, orderId]);

  const runAction = async (path: string) => {
    if (!order) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await apiClient.post(`/admin/gift-orders/${order.id}/${path}`);
      await loadOrder(order.id);
      onOrderUpdated?.();
    } catch {
      setActionError('That action failed. Check the API response or try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const showRetryCapture =
    order?.fulfillmentStatus === FulfillmentStatus.Placed
    && order?.paymentStatus === PaymentStatus.CaptureFailed;
  const showVoid =
    order?.fulfillmentStatus === FulfillmentStatus.Pending
    && order?.paymentStatus === PaymentStatus.Authorized;
  const showCancel =
    order?.fulfillmentStatus === FulfillmentStatus.Pending
    || order?.fulfillmentStatus === FulfillmentStatus.Failed;
  const showPlaceNow =
    order
    && !order.goodyOrderId
    && order.fulfillmentStatus !== FulfillmentStatus.Cancelled;
  const showRetryPlace =
    order?.fulfillmentStatus === FulfillmentStatus.Failed
    && order?.paymentStatus === PaymentStatus.Voided
    && !order.goodyOrderId;
  const showRefund =
    order?.paymentStatus === PaymentStatus.Captured
    || order?.paymentStatus === PaymentStatus.PartiallyRefunded;

  const formatCurrency = (amount: number | null | undefined) =>
    amount == null
      ? '—'
      : amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  return (
    <AppModal
      id="admin-gift-order-detail"
      opened={opened}
      onClose={onClose}
      title={order ? `Gift order #${order.id}` : 'Gift order'}
      size={AppModalSizes.form}
      zIndex={zIndex}
      footer={
        order && (showPlaceNow || showRetryPlace || showRetryCapture || showVoid || showCancel || showRefund) ? (
          <Group justify="flex-end" wrap="wrap">
            {showPlaceNow && (
              <Button
                color={actionColors.sendNow}
                loading={actionLoading}
                onClick={() => runAction('place-now')}
              >
                Place now
              </Button>
            )}
            {showRetryPlace && (
              <Button
                color={actionColors.sendNow}
                loading={actionLoading}
                onClick={() => runAction('retry-place')}
              >
                Retry place
              </Button>
            )}
            {showRetryCapture && (
              <Button
                variant="light"
                loading={actionLoading}
                onClick={() => runAction('retry-capture')}
              >
                Retry capture
              </Button>
            )}
            {showVoid && (
              <Button
                variant="light"
                color={actionColors.delete}
                loading={actionLoading}
                onClick={() => runAction('void-authorization')}
              >
                Void authorization
              </Button>
            )}
            {showCancel && (
              <Button
                variant="default"
                loading={actionLoading}
                onClick={() => runAction('cancel')}
              >
                Cancel order
              </Button>
            )}
            {showRefund && (
              <Button
                variant="light"
                color={actionColors.delete}
                loading={actionLoading}
                onClick={() => runAction('refund-customer')}
              >
                Refund customer
              </Button>
            )}
          </Group>
        ) : null
      }
    >
      {actionError && (
        <Alert color={brandColors.danger} mb="md">
          {actionError}
        </Alert>
      )}
      {loading && (
        <Center py="xl">
          <Loader />
        </Center>
      )}
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color={brandColors.danger}>
          {error}
        </Alert>
      )}
      {order && !loading && (
        <Stack gap="md">
          <Group justify="space-between">
            <OrderStatusBadges
              fulfillmentStatus={order.fulfillmentStatus}
              paymentStatus={order.paymentStatus}
              size="sm"
            />
          </Group>

          {order.imageUrl && (
            <Image src={order.imageUrl} alt={order.productName} mah={160} fit="contain" />
          )}

          <DetailRow label="Product">
            <Text size="sm">{order.productName}</Text>
          </DetailRow>
          <DetailRow label="Occasion">
            <Text size="sm">{order.eventTypeName}</Text>
          </DetailRow>
          <DetailRow label="Requested date">
            <Text size="sm">{formatCalendarDate(order.requestedDate)}</Text>
          </DetailRow>
          <DetailRow label="Scheduled total">
            <Text size="sm">{formatCurrency(order.scheduledChargeUsd)}</Text>
          </DetailRow>
          <DetailRow label="Goody order id">
            <Text size="sm">{order.goodyOrderId ?? '—'}</Text>
          </DetailRow>
          <DetailRow label="Goody status">
            <Text size="sm">{order.goodyStatus ?? '—'}</Text>
          </DetailRow>
          {order.trackingUrl && (
            <DetailRow label="Tracking">
              <Text size="sm" component="a" href={order.trackingUrl} target="_blank" rel="noreferrer">
                Open tracking
              </Text>
            </DetailRow>
          )}

          <Divider label="Recipient" />
          <DetailRow label="Name">
            <Text size="sm">
              {order.recipientFirstName} {order.recipientLastName}
            </Text>
          </DetailRow>
          {order.recipientAddress1 && (
            <DetailRow label="Address">
              <Stack gap={0} align="flex-end">
                <Text size="sm">{order.recipientAddress1}</Text>
                <Text size="sm">
                  {order.recipientCity}, {getStateName(order.recipientState ?? '', 'US')}{' '}
                  {order.recipientPostalCode}
                </Text>
              </Stack>
            </DetailRow>
          )}
          {order.note && (
            <DetailRow label="Note">
              <Text size="sm">{order.note}</Text>
            </DetailRow>
          )}

          <Divider label="Customer" />
          <DetailRow label="Account">
            <Group gap={6} justify="flex-end">
              <Text size="sm">{order.customerName ?? order.customerEmail}</Text>
              {order.isDemoAccount && <DemoAccountPill />}
            </Group>
          </DetailRow>
          <DetailRow label="Email">
            <Text size="sm">{order.customerEmail}</Text>
          </DetailRow>

          {order.pricing && <AdminGiftOrderPricingSection pricing={order.pricing} />}

          <Divider label="Payment" />
          <DetailRow label="Authorized">
            <Text size="sm">{formatCurrency(order.authorizedAmount)}</Text>
          </DetailRow>
          <DetailRow label="Captured">
            <Text size="sm">{formatCurrency(order.capturedAmount)}</Text>
          </DetailRow>
          {order.cardLast4 && (
            <DetailRow label="Card">
              <Text size="sm">•••• {order.cardLast4}</Text>
            </DetailRow>
          )}

          {order.isRetryingPlacement && (
            <>
              <Divider label="Placement" />
              <DetailRow label="Status">
                <Text size="sm">{formatStatusLabel(order.fulfillmentStatus)} (retrying)</Text>
              </DetailRow>
              <DetailRow label="Attempts">
                <Text size="sm">{order.placementAttemptCount}</Text>
              </DetailRow>
              {order.lastPlacementError && (
                <DetailRow label="Last error">
                  <Text size="sm">{order.lastPlacementError}</Text>
                </DetailRow>
              )}
            </>
          )}

          <DetailRow label="Created">
            <Text size="sm">{formatInstantLocalShort(order.createdAt)}</Text>
          </DetailRow>
        </Stack>
      )}
    </AppModal>
  );
};
